/**
 * Shapiro–Wilk 正規性検定（R stats::shapiro.test / AS R94 準拠）
 * 計算式は docs/systems/keisuke/FORMULAS.md を参照
 */

import { inverseNormalCDF, normalCDF } from './probit';

export interface ShapiroWilkResult {
  W: number;
  pValue: number;
  n: number;
}

const SMALL = 1e-19;

const G = [-2.273, 0.459];
const C1 = [0, 0.221157, -0.147981, -2.07119, 4.434685, -2.706056];
const C2 = [0, 0.042981, -0.293762, -1.752461, 5.682633, -3.582633];
const C3 = [0.544, -0.39978, 0.025054, -0.0006714];
const C4 = [1.3822, -0.77857, 0.062767, -0.0020322];
const C5 = [-1.5861, -0.31082, -0.083751, 0.0038915];
const C6 = [-0.4803, -0.082676, 0.0030302];

/**
 * Shapiro–Wilk 検定（3 <= n <= 5000）
 */
export function shapiroWilkTest(data: number[]): ShapiroWilkResult {
  const values = data.filter((v) => Number.isFinite(v));
  const n = values.length;
  if (n < 3) {
    throw new Error('Shapiro–Wilk検定には3点以上のデータが必要です');
  }
  if (n > 5000) {
    throw new Error('Shapiro–Wilk検定は5000点以下を想定しています');
  }

  const sorted = [...values].sort((a, b) => a - b);
  const { w, pw, fault } = swilkAsR94(sorted, n);
  if (fault === 6) {
    throw new Error('データの範囲が0です（すべて同じ値）');
  }
  if (fault !== 0 && fault !== 7) {
    throw new Error(`Shapiro–Wilk検定でエラーが発生しました (code ${fault})`);
  }

  return { W: w, pValue: pw, n };
}

/** R swilk.c (AS R94) の移植 */
function swilkAsR94(x: number[], n: number): { w: number; pw: number; fault: number } {
  const nn2 = Math.floor(n / 2);
  const a = new Array<number>(nn2 + 1).fill(0);

  let pw = 1;
  if (n < 3) {
    return { w: 1, pw, fault: 1 };
  }

  const an = n;

  if (n === 3) {
    a[1] = Math.SQRT1_2;
  } else {
    const an25 = an + 0.25;
    let summ2 = 0;
    for (let i = 1; i <= nn2; i += 1) {
      a[i] = inverseNormalCDF((i - 0.375) / an25);
      summ2 += a[i]! * a[i]!;
    }
    summ2 *= 2;
    const ssumm2 = Math.sqrt(summ2);
    const rsn = 1 / Math.sqrt(an);
    const a1 = poly(C1, 6, rsn) - a[1]! / ssumm2;

    let i1: number;
    let fac: number;
    if (n > 5) {
      i1 = 3;
      const a2 = -a[2]! / ssumm2 + poly(C2, 6, rsn);
      fac = Math.sqrt(
        (summ2 - 2 * (a[1]! * a[1]! + a[2]! * a[2]!)) / (1 - 2 * (a1 * a1 + a2 * a2))
      );
      a[2] = a2;
    } else {
      i1 = 2;
      fac = Math.sqrt((summ2 - 2 * (a[1]! * a[1]!)) / (1 - 2 * (a1 * a1)));
    }
    a[1] = a1;
    for (let i = i1; i <= nn2; i += 1) {
      a[i] = a[i]! / -fac;
    }
  }

  const range = x[n - 1]! - x[0]!;
  if (range < SMALL) {
    return { w: 0, pw: 1, fault: 6 };
  }

  let fault = 0;
  let xx = x[0]! / range;
  let sx = xx;
  let sa = -a[1]!;
  for (let i = 1, j = n - 1; i < n; j -= 1) {
    const xi = x[i]! / range;
    if (xx - xi > SMALL) {
      fault = 7;
    }
    sx += xi;
    i += 1;
    // R swilk.c は sx 加算後に i をインクリメントし、更新後の i で a を参照する。
    // ここで増分前の i を使うと平均 sa が a[1]/n だけずれ、W が系統的に過小になる。
    if (i !== j) {
      sa += Math.sign(i - j) * a[Math.min(i, j)]!;
    }
    xx = xi;
  }

  sa /= n;
  sx /= n;
  let ssa = 0;
  let ssx = 0;
  let sax = 0;
  for (let i = 0, j = n - 1; i < n; i += 1, j -= 1) {
    const asa =
      i !== j ? Math.sign(i - j) * a[1 + Math.min(i, j)]! - sa : -sa;
    const xsx = x[i]! / range - sx;
    ssa += asa * asa;
    ssx += xsx * xsx;
    sax += asa * xsx;
  }

  const ssassx = Math.sqrt(ssa * ssx);
  const w1 = ((ssassx - sax) * (ssassx + sax)) / (ssa * ssx);
  const w = 1 - w1;

  if (n === 3) {
    const pi6 = 1.90985931710274;
    const stqr = 1.0471975511966;
    pw = pi6 * (Math.asin(Math.sqrt(w)) - stqr);
    if (pw < 0) pw = 0;
    return { w, pw, fault };
  }

  const y = Math.log(w1);
  const lnan = Math.log(an);
  let m: number;
  let s: number;
  if (n <= 11) {
    const gamma = poly(G, 2, an);
    if (y >= gamma) {
      pw = 1e-99;
      return { w, pw, fault };
    }
    const yy = -Math.log(gamma - y);
    m = poly(C3, 4, an);
    s = Math.exp(poly(C4, 4, an));
    pw = 1 - normalCDF((yy - m) / s);
    return { w, pw, fault };
  }

  m = poly(C5, 4, lnan);
  s = Math.exp(poly(C6, 3, lnan));
  pw = 1 - normalCDF((y - m) / s);
  return { w, pw, fault };
}

function poly(cc: number[], nord: number, x: number): number {
  let retVal = cc[0]!;
  if (nord > 1) {
    let p = x * cc[nord - 1]!;
    for (let j = nord - 2; j > 0; j -= 1) {
      p = (p + cc[j]!) * x;
    }
    retVal += p;
  }
  return retVal;
}
